<template>
    <div class="ppv2-space-y-6">
        <!-- Adresse complète -->
        <BaseInput
            v-model="formData.head_office_address"
            :label="$t('configuration.locationForm.fields.address.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.address.description')"
            :placeholder="$t('configuration.locationForm.fields.address.placeholder')"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </template>
        </BaseInput>

        <!-- Complément d'adresse -->
        <BaseInput
            v-model="formData.head_office_complement_address"
            :label="$t('configuration.locationForm.fields.complement.label')"
            :tooltipMsg="$t('configuration.locationForm.fields.complement.description')"
            :placeholder="$t('configuration.locationForm.fields.complement.placeholder')"
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Ville -->
        <BaseInput
            v-model="formData.head_office_city"
            :label="$t('configuration.locationForm.fields.city.label')"
            :placeholder="$t('configuration.locationForm.fields.city.placeholder')"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Code postal -->
        <BaseInput
            v-model="formData.head_office_postal_code"
            :label="$t('configuration.locationForm.fields.postalCode.label')"
            :placeholder="$t('configuration.locationForm.fields.postalCode.placeholder')"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                </svg>
            </template>
        </BaseInput>

        <!-- Pays -->
        <BaseSelect
            v-model="formData.head_office_country_id"
            :label="$t('configuration.locationForm.fields.country.label')"
            :options="countries"
            option-label="name"
            option-value="id"
            required
        >
            <template #leftIcon>
                <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </template>
        </BaseSelect>

        <!-- Interactive Map -->
        <InteractiveMap
            ref="interactiveMapRef"
            :label="$t('configuration.locationForm.interactiveMap.label')"
            :tooltipMsg="$t('configuration.locationForm.interactiveMap.description')"
            @coordinates-updated="handleCoordinates"
        />
    </div>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import InteractiveMap from '../../../Shared/InteractiveMap.vue'
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'

export default {
    name: 'LocationFormOffice',
    props: {
        card: {
            required: true,
        },
        countries: {
            required: true,
        },
    },
    components: {
        BaseInput,
        BaseSelect,
        InfoMessageBox,
        InteractiveMap,
        BaseRadioGroup,
    },
    data() {
        return {
            formData: {
                head_office_address: '',
                head_office_complement_address: '',
                head_office_city: '',
                head_office_postal_code: '',
                head_office_country_id: null,
                head_office_map_coords: null,
            },
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        isBillingAddressSameHeadOfficeOptions() {
            return [
                { label: this.$t('configuration.tax.fields.vatApplicable.options.yes'), value: true },
                { label: this.$t('configuration.tax.fields.vatApplicable.options.no'), value: false },
            ]
        },
        fullAddressQuery() {
            const { head_office_address, head_office_city, head_office_postal_code, head_office_country_id } = this.formData
            return [head_office_address, head_office_city, head_office_postal_code, head_office_country_id]
                .filter(Boolean)
                .join(', ')
        },
    },
    watch: {
        // 'formData.pays.id'(newId) {
        //     const match = this.countries.find((c) => c.id === newId)
        //     if (match) {
        //         this.formData.head_office_pays.name = match.name
        //     } else {
        //         this.formData.head_office_pays.name = ''
        //     }
        // },
        fullAddressQuery: 'geocodeAddress',
        establishment: {
            handler() {
                this.initializeForm()
            },
            immediate: true,
            deep: true,
        },
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
            },
            deep: true,
        },
    },
    created() {
        if (this.formData?.head_office_map_coords?.lat && this.formData?.head_office_map_coords?.lng) {
            const { lat, lng } = this.formData?.head_office_map_coords
            setTimeout(() => {
                this.$refs.interactiveMapRef?.updateMapCenter(lat, lng)
            }, 1000)
        }
    },
    methods: {
        handleCoordinates(coords) {
            this.formData.head_office_map_coords = coords
        },
        initializeForm() {
            const fields = [
                'head_office_address',
                'head_office_complement_address',
                'head_office_city',
                'head_office_postal_code',
                'head_office_country_id',
                'head_office_map_coords',
            ]
            fields.forEach((field) => {
                if (this.establishment.hasOwnProperty(field)) {
                    this.formData[field] = this.establishment[field]
                }
            })
        },
        getFormData() {
            return this.formData
        },
        validateForm() {
            const required = ['head_office_address', 'head_office_city', 'head_office_postal_code', 'head_office_country_id']
            return required.every((field) => {
                const val = this.formData[field]
                return val && typeof val === 'string' ? val.trim() !== '' : val !== null
            })
        },
        resetForm() {
            this.formData = {
                head_office_address: '',
                head_office_complement_address: '',
                head_office_city: '',
                head_office_postal_code: '',
                head_office_country_id: null,
                head_office_map_coords: null,
            }
        },
        async geocodeAddress() {
            const query = this.fullAddressQuery
            if (!query) return
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
                )
                const data = await response.json()

                if (data && data.length > 0) {
                    const { lat, lon } = data[0]
                    const coords = { lat: parseFloat(lat), lng: parseFloat(lon) }

                    this.formData.head_office_map_coords = coords

                    // Envoyer les nouvelles coordonnées à la carte
                    this.$refs.interactiveMapRef?.updateMapCenter(coords.lat, coords.lng)
                }
            } catch (err) {
                console.error('Geocoding failed:', err)
            }
        },
    },
}
</script>
